import { use, useCallback, useMemo, useState, useEffect, type FC } from 'react';
import { useDropdown } from '@/utils/dashboard/useDropdown';
import { DropdownWrapper } from '@/utils/dashboard/DropdownWrapper';
import ModelSelector from '../controls/ModelSelector';
import PromptInput from '../controls/PromptInput';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { DeviceContext } from '@/hooks/common/DeviceContext';
import { PicassoTitle } from '../../ui/BrandingComponents';
import { ExpandableText } from '@/components/ui/ExpandableText';
import { Transcription } from '@/hooks/dashboard/useTranscription';
import { TogglePage } from '../ui/TogglePage';
import { IMAGE_MODELS, supportsMultipleImages, AIModel } from '@/constants/models';
import {
  MobileModelSelector,
  MobilePromptSettingsDrawer,
  MobileStackedImages,
  MobileVideoPreview,
} from './MobileView/PicassoControls';

// Loading Dots Component
const LoadingDots: FC = () => {
  const [dots, setDots] = useState('.');
  
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev === '...') return '.';
        return prev + '.';
      });
    }, 500);
    
    return () => clearInterval(interval);
  }, []);
  
  return <span>Generating{dots}</span>;
};

// Media validation configurations
const IMAGE_VALIDATION = {
  maxSize: 25 * 1024 * 1024, // 100MB 
  supportedTypes: ['image/jpeg', 'image/jpg', 'image/png'],
  supportedExtensions: ['.jpg', '.jpeg', '.png']
};

const WHISPER_VALIDATION = {
  maxSize: 100 * 1024 * 1024, // 100MB
};

// Shared styling constants
const GRADIENT_BORDER_STYLE = {
  border: "2px solid transparent",
  background: "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box",
};

const MEDIA_PREVIEW_STYLES = {
  className: "w-[130px] h-[163px] rounded-[13px] object-cover",
  style: {
    border: "1px solid transparent",
    background: "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
    
  }
};

const MAVEN_PRO_FONT = 'Maven Pro';

const BUTTON_BASE_STYLE = {
  fontFamily: MAVEN_PRO_FONT,
  fontSize: '16px',
  fontWeight: 600,
};

const MODAL_BUTTON_SECONDARY = {
  ...BUTTON_BASE_STYLE,
  border: '2px solid #E5E7EB',
  background: '#FFFFFF',
  color: '#6B7280',
};

const MODAL_BUTTON_PRIMARY = {
  ...BUTTON_BASE_STYLE,
  background: '#1F2937',
};

const ASPECT_RATIO_SECTION_HEADER = {
  fontFamily: MAVEN_PRO_FONT,
  fontSize: '16px',
  fontWeight: 400,
  textAlign: 'left' as const,
};

const ASPECT_RATIO_BUTTON_STYLE = {
  fontFamily: MAVEN_PRO_FONT,
  borderRadius: '8px',
  padding: '8px 12px',
  gap: '8px',
  justifyContent: 'flex-start' as const,
};

const ASPECT_RATIO_LABEL_STYLE = {
  fontFamily: MAVEN_PRO_FONT,
  fontWeight: 400,
  fontStyle: 'normal' as const,
  fontSize: '16px',
  lineHeight: '100%',
  letterSpacing: '0%',
};

const SECTION_HEADER_CONTAINER_STYLE = {
  marginBottom: '6px',
  paddingLeft: '12px'
};

const FLEX_COLUMN_GAP_4_STYLE = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '4px'
};

const DROPDOWN_MENU_BASE_STYLE = {
  borderRadius: '25px',
  paddingTop: '14px',
  paddingRight: '8px',
  paddingBottom: '14px',
  paddingLeft: '8px',
  opacity: 1,
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '12px'
};

const REMOVE_BUTTON_CLASS = "absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs transition-colors shadow-lg z-10";

// Reusable Remove Button Component
const RemoveButton: FC<{ onClick: () => void; label?: string }> = ({ onClick, label = "Remove" }) => (
  <button
    onClick={onClick}
    className={REMOVE_BUTTON_CLASS}
    aria-label={label}
  >
    ×
  </button>
);

// Enhanced Aspect Ratio Button Component - supports both aspect ratios and resolutions
const AspectRatioButton: FC<{
  ratio?: { value: string; label: string; width: number; height: number };
  label?: string;
  isSelected: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  justifyContent?: 'flex-start' | 'center';
  icon?: React.ReactNode;
}> = ({ ratio, label, isSelected, onClick, justifyContent = 'flex-start', icon }) => (
  <button
    onClick={onClick}
    className="w-full transition-colors flex items-center"
    style={{
      ...ASPECT_RATIO_BUTTON_STYLE,
      justifyContent,
      border: isSelected ? "2px solid transparent" : "none",
      background: isSelected 
        ? "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box"
        : "transparent",
      outline: "none",
    }}
    onMouseEnter={(e) => {
      if (!isSelected) {
        e.currentTarget.style.background = "#F9FAFB";
      }
    }}
    onMouseLeave={(e) => {
      if (!isSelected) {
        e.currentTarget.style.background = "transparent";
      }
    }}
  >
    {icon}
    {ratio ? (
      <>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width={ratio.width} 
          height={ratio.height} 
          viewBox={`0 0 ${ratio.width} ${ratio.height}`} 
          fill="none"
        >
          <rect 
            x="1" 
            y="1" 
            width={ratio.width - 2} 
            height={ratio.height - 2} 
            rx="3" 
            stroke="currentColor" 
            strokeWidth="2"
          />
        </svg>
        <span style={ASPECT_RATIO_LABEL_STYLE}>{ratio.label}</span>
      </>
    ) : (
      <span style={ASPECT_RATIO_LABEL_STYLE}>{label}</span>
    )}
  </button>
);

// File validation helpers
const validateWhisperFile = (file: File, setError: (error: string) => void): boolean => {
  if (file.size > WHISPER_VALIDATION.maxSize) {
    setError('File must be less than 100 MB for transcription');
    return false;
  }
  return true;
};

const validateImageFile = (file: File, setError: (error: string) => void): boolean => {
  if (!IMAGE_VALIDATION.supportedTypes.includes(file.type.toLowerCase())) {
    setError('Please select a valid image file. Supported formats: JPEG, JPG, PNG');
    return false;
  }

  if (file.size > IMAGE_VALIDATION.maxSize) {
    setError('Image file size must be less than 50 MB');
    return false;
  }

  const fileName = file.name.toLowerCase();
  if (!IMAGE_VALIDATION.supportedExtensions.some(ext => fileName.endsWith(ext))) {
    setError('Please select a valid image file with a supported extension');
    return false;
  }

  return true;
};

// Media preview components
const VideoPreview: FC<{
  previewUrl: string;
  onRemove: () => void;
  isMobile: boolean;
  fileName?: string;
  fileSize?: string;
}> = ({
  previewUrl,
  onRemove,
  isMobile,
  fileName = "Video file.mp4",
  fileSize = "1 GB",
}) => {
  if (isMobile) {
    return <MobileVideoPreview
      previewUrl={previewUrl}
      onRemove={onRemove}
      fileName={fileName}
      fileSize={fileSize}
    />;
  }

  return (<div className="relative flex items-center justify-center">
    <video
      src={previewUrl}
      className={MEDIA_PREVIEW_STYLES.className}
      style={MEDIA_PREVIEW_STYLES.style}
      controls={false}
      muted
    >
      <track kind="captions" src="" label="No captions available" />
    </video>
    <RemoveButton onClick={onRemove} label="Remove video" />
  </div>)
};

const AudioPreview: FC<{
  previewUrl: string;
  onRemove: () => void;
  isMobile: boolean;
}> = ({
  previewUrl,
  onRemove,
  isMobile,
}) => (
    <div className={`relative flex items-center justify-center ${isMobile && "w-full gap-2"}`}>
      <audio
        src={previewUrl}
        controls
        className={`${isMobile ? "w-[calc(100%-63px)]" : "w-[130px]"} rounded-md`}
      >
        <track kind="captions" src="" label="No captions available" default />
      </audio>
      {
        isMobile ? (
        <button
          onClick={onRemove}
          className="flex-shrink-0 p-2 transition-colors bg-transparent text-gray-500"
          aria-label="Remove image"
        >
          <DeleteOutlinedIcon />
        </button>
        ) : <RemoveButton onClick={onRemove} label="Remove audio" />
      }
    </div>
  );


// Image Manager Modal Component
const ImageManagerModal: FC<{
  isOpen: boolean;
  onClose: () => void;
  imagePreviews: string[];
  onRemoveImage: (index: number) => void;
  onAddImage: () => void;
  totalImages: number;
  maxImages?: number;
}> = ({ isOpen, onClose, imagePreviews, onRemoveImage, onAddImage, totalImages: _totalImages, maxImages = 3 }) => {
  if (!isOpen) return null;

  return (
    <dialog
      open
      className="fixed inset-0 bg-transparent flex items-center justify-center z-50 border-0 p-0 m-0 max-w-none max-h-none w-full h-full"
      aria-modal="true"
      aria-label="Image manager modal"
      style={{ background: 'rgba(0, 0, 0, 0.5)' }}
    >
      <div
        className="bg-white border-0 p-0"
        aria-labelledby="modal-title"
        style={{
          width: '621px',
          borderRadius: '25px',
          paddingTop: '32px',
          paddingRight: '32px',
          paddingBottom: '32px',
          paddingLeft: '32px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          opacity: 1,
          position: 'relative',
          margin: '0',
          maxWidth: 'none',
          maxHeight: 'none',
        }}
      >
        {/* Header */}
        <div className="flex justify-between items-center">
          <h2 
            id="modal-title"
            className="text-gray-900"
            style={{
              fontFamily: 'Maven Pro',
              fontSize: '24px',
              fontWeight: 750,
              lineHeight: '100%',
            }}
          >
            Uploaded Images
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
            }}
          >
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Separator Line */}
        <div style={{ height: '1px', background: '#E5E7EB', margin: '0 -32px' }} />

        {/* Images Grid with Counter */}
        <div className="flex flex-col gap-3">
          {/* Image Counter Badge - Top Right */}
          <div className="flex justify-end">
            <div 
              className="flex items-center gap-1.5"
              style={{
                background: '#EEF2FF',
                color: '#6366F1',
                padding: '6px 12px',
                fontSize: '14px',
                fontFamily: 'Maven Pro, sans-serif',
                fontWeight: 600,
                borderRadius: '8px',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
              </svg>
              {imagePreviews.length}/{maxImages}
            </div>
          </div>

          {/* Images Grid */}
          <div className="grid grid-cols-3 gap-3">
            {/* Display uploaded images */}
            {imagePreviews.map((preview, index) => (
              <div 
                key={`modal-image-${index}-${preview.substring(0, 50)}`} 
                className="relative"
                style={{
                  width: '172px',
                  height: '217px',
                }}
              >
                <img
                  src={preview}
                  alt={`Uploaded ${index + 1}`}
                  className="w-full h-full object-cover"
                  style={{
                    borderRadius: '16px',
                  }}
                />
                <button
                  onClick={() => onRemoveImage(index)}
                  className="absolute text-white hover:text-gray-200 transition-colors"
                  aria-label="Remove image"
                  style={{
                    top: '8px',
                    right: '8px',
                    background: 'transparent',
                    padding: 0,
                    width: '16px',
                    height: '16px',
                  }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
            
            {/* Display empty placeholder boxes for remaining slots */}
            {Array.from({ length: Math.max(0, maxImages - imagePreviews.length) }).map((_, index) => (
              <button
                key={`empty-slot-${imagePreviews.length}-${index}`}
                onClick={onAddImage}
                className="flex items-center justify-center transition-all hover:opacity-80"
                style={{
                  width: '172px',
                  height: '217px',
                  borderRadius: '16px',
                  border: '2px dashed #C7D2FE',
                  background: '#EEF2FF',
                }}
              >
                {/* Plus icon with text */}
                <div className="flex flex-col items-center gap-2">
                  <svg className="w-10 h-10 text-[#818CF8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span 
                    className="text-[#818CF8]"
                    style={{
                      fontFamily: 'Maven Pro',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    Add image
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Separator Line */}
        <div style={{ height: '1px', background: '#E5E7EB', margin: '0 -32px' }} />

        {/* Footer Buttons - Aligned to Right */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="py-3 px-8 rounded-full font-medium transition-all hover:opacity-80"
            style={MODAL_BUTTON_SECONDARY}
          >
            Close
          </button>
          <button
            onClick={onClose}
            className="py-3 px-8 rounded-full text-white font-medium transition-all hover:opacity-90"
            style={MODAL_BUTTON_PRIMARY}
          >
            Confirm
          </button>
        </div>
      </div>
    </dialog>
  );
};

// Stacked Images Preview Component for Multiple Images (Gemini 2.5)
const StackedImagesPreview: FC<{ 
  imagePreviews: string[]; 
  totalImages?: number;
  onEdit?: () => void;
  onRemoveImage?: (index: number) => void;
  onAddImage?: () => void;
  isMobile: boolean;
}> = ({
  imagePreviews,
  totalImages = 3,
  onEdit,
  onRemoveImage,
  onAddImage,
  isMobile,
}) => {
  const imageCount = imagePreviews.length;
  
  // Mobile horizontal layout
  if (isMobile) {
    return (
      <MobileStackedImages
        imagePreviews={imagePreviews} 
        onRemoveImage={onRemoveImage}
        onAddImage={onAddImage}
        totalImages={totalImages}
      />
    );
  }

  // Desktop stacked layout (original)
  return (
    <div className="relative flex items-center justify-center" style={{ width: '180px', height: '200px' }}>
      {imagePreviews.map((preview, index) => (
        <div
          key={`image-${index}-${preview.substring(0, 50)}`}
          className="absolute transition-all duration-300"
          style={{
            zIndex: index + 1,
            transform: `rotate(${(index - Math.floor(imageCount / 2)) * 8}deg) translateY(${index * -2}px)`,
            left: `${index * 12}px`,
          }}
        >
          <div className="relative">
            <img
              src={preview}
              alt={`Selected input ${index + 1}`}
              className={MEDIA_PREVIEW_STYLES.className}
              style={MEDIA_PREVIEW_STYLES.style}
            />
            {/* Remove button for single image - styled like edit modal */}
            {totalImages === 1 && onRemoveImage && (
              <button
                onClick={() => onRemoveImage(0)}
                className="absolute text-white hover:text-gray-200 transition-colors"
                aria-label="Remove image"
                style={{
                  top: '8px',
                  right: '8px',
                  background: 'transparent',
                  padding: 0,
                  width: '16px',
                  height: '16px',
                  zIndex: 10,
                }}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {/* Edit/Pencil button centered on topmost image - only for multiple images */}
            {onEdit && totalImages > 1 && index === imageCount - 1 && (
              <button
                onClick={onEdit}
                className="absolute rounded-full flex items-center justify-center transition-all shadow-lg hover:opacity-90"
                aria-label="Edit images"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 10,
                  width: '32px',
                  height: '32px',
                  padding: 0,
                  background: 'transparent',
                }}
              >
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="16" cy="16" r="16" fill="white" fillOpacity="0.6"/>
                  <path d="M8.5 23.5V19.9583L19.5 8.97917C19.6667 8.82639 19.8507 8.70833 20.0521 8.625C20.2535 8.54167 20.4653 8.5 20.6875 8.5C20.9097 8.5 21.125 8.54167 21.3333 8.625C21.5417 8.70833 21.7222 8.83333 21.875 9L23.0208 10.1667C23.1875 10.3194 23.309 10.5 23.3854 10.7083C23.4618 10.9167 23.5 11.125 23.5 11.3333C23.5 11.5556 23.4618 11.7674 23.3854 11.9687C23.309 12.1701 23.1875 12.3542 23.0208 12.5208L12.0417 23.5H8.5ZM20.6667 12.5L21.8333 11.3333L20.6667 10.1667L19.5 11.3333L20.6667 12.5Z" fill="#2C2C2C"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      ))}
      
      {/* Image counter - bottom center with light purple background */}
      {/* Position it under the center of the image stack */}
      <div 
        className="absolute font-medium flex items-center justify-center gap-1.5 shadow-md"
        style={{
          bottom: '-35px',
          left: `${65 + ((imageCount - 1) * 12) / 2}px`, // Center under the image stack (65px = half of 130px image width)
          transform: 'translateX(-50%)',
          zIndex: 100,
          background: '#EEF2FF',
          color: '#6366F1',
          padding: '6px 12px',
          fontSize: '14px',
          fontFamily: 'Maven Pro, sans-serif',
          fontWeight: 600,
          borderRadius: '8px',
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/>
        </svg>
        {imageCount}/{totalImages}
      </div>
    </div>
  );
};

const getImageUploadTitle = (selectedModel: string, pageValue: string): string => {
  if (IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number])) {
    return 'Upload an image for editing or style transfer';
  }
  if (pageValue === 'Images') {
    return 'Upload an image';
  }
  return 'Upload an image to guide video generation';
};

const isAudioEnabledForModel = (selectedModel: string): boolean => 
  selectedModel === 'Veo 3' || selectedModel === 'Veo 3 Fast' || selectedModel === 'Veo 3.1' || selectedModel === 'Veo 3.1 Fast';

// File handling helpers
const handleWhisperFile = (file: File, onFileSelection: (file: File) => void): void => {
  const url = URL.createObjectURL(file);
  const mediaType = file.type.startsWith('video') ? 'video' : 'audio';
  const media = document.createElement(mediaType);
  media.preload = 'metadata';
  media.src = url;

  const handleSuccess = () => {
    URL.revokeObjectURL(url);
    onFileSelection(file);
  };

  media.onloadedmetadata = handleSuccess;
  media.onerror = handleSuccess;
};

const handleImageFile = (file: File, onImageUpload: (file: File) => void, setError: (error: string) => void): void => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  
  img.onload = () => {
    URL.revokeObjectURL(url);
    onImageUpload(file);
  };
  
  img.onerror = () => {
    URL.revokeObjectURL(url);
    setError('Invalid image file. Please select a valid image.');
  };
  
  img.src = url;
};

const TranscriptionInput = ({
  handleImageClick,
  imageUploadTitle,
  selectedModel,
  isTranscriptionLoading,
  onGenerate,
  isTranscription,
  isGenerating,
  previewUrl,
  isMobile,
}:{
    handleImageClick: () => void,
    imageUploadTitle?: string,
    selectedModel: string,
    isTranscriptionLoading: boolean | undefined,
    onGenerate: () => void;
    isTranscription: boolean;
    isGenerating: boolean;
    previewUrl: string | null;
    isMobile: boolean;
}) => {
  return (
    // Whisper (Transcript) - Single Row Layout
    <div className="flex items-center w-full gap-4">
      {/* File Attachment Icon - Paperclip */}
      <button
        onClick={handleImageClick}
        className="flex-shrink-0 p-2.5 rounded-full transition-all duration-300 hover:scale-105 focus:outline-none"
        title={imageUploadTitle}
        style={{
          border: "2px solid transparent",
          background: "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box",
          outline: 'none',
        }}
      >
        {isMobile ? 
          <div className='flex items-center gap-1'>
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21.0938 4.6875H3.90625C3.47478 4.6875 3.125 5.03728 3.125 5.46875V19.5312C3.125 19.9627 3.47478 20.3125 3.90625 20.3125H21.0938C21.5252 20.3125 21.875 19.9627 21.875 19.5312V5.46875C21.875 5.03728 21.5252 4.6875 21.0938 4.6875Z" stroke="#B2B2B2" />
              <path d="M15.2344 9.09375C15.6054 9.09375 15.9062 9.39456 15.9062 9.76562C15.9062 10.1367 15.6054 10.4375 15.2344 10.4375C14.8633 10.4375 14.5625 10.1367 14.5625 9.76562C14.5625 9.39456 14.8633 9.09375 15.2344 9.09375Z" fill="#B2B2B2" stroke="#B2B2B2"/>
              <path d="M14.3867 16.0158L16.8955 13.5099C17.042 13.3635 17.2406 13.2812 17.4478 13.2812C17.6549 13.2812 17.8535 13.3635 18 13.5099L21.876 17.3888" stroke="#B2B2B2" />
              <path d="M3.125 16.4742L8.43164 11.1666C8.5042 11.094 8.59036 11.0364 8.6852 10.9971C8.78005 10.9577 8.88171 10.9375 8.98438 10.9375C9.08704 10.9375 9.1887 10.9577 9.28355 10.9971C9.37839 11.0364 9.46455 11.094 9.53711 11.1666L18.6826 20.3131" stroke="#B2B2B2" />
            </svg>
          </div>:
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
          </svg>
        }
      </button>

      {/* Model Label - Non-interactive */}
      {
        !isMobile && (
        <>
          <div 
            className="flex-shrink-0 text-black font-medium"
            style={{
              background: "linear-gradient(white, white) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box",
              border: '2px solid transparent',
              borderRadius: '100px',
              width: '149px',
              height: '42px',
              paddingTop: '9px',
              paddingRight: '27px',
              paddingBottom: '9px',
              paddingLeft: '27px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Maven Pro',
              fontSize: '16px',
            }}
          >
            {selectedModel}
          </div>

          {/* Placeholder Text */}
          <span className="text-base text-gray-400" style={{ fontSize: '16px' }}>
            {isTranscriptionLoading ? <LoadingDots /> : 'Attach video and wait for your transcript'}
          </span>
        </>
        )
      }

      {/* Arrow Submit Button */}
      <MainGenerateButton
        onGenerate={onGenerate}
        isTranscription={isTranscription}
        isGenerating={isGenerating}
        isTranscriptionLoading={isTranscriptionLoading}
        selectedModel={selectedModel}
        previewUrl={previewUrl}
      />
    </div>
  )
}

// Media preview helper
// Helper to render Whisper file previews
const renderWhisperPreview = (
  file: File | null,
  fileType: string | null,
  previewUrl: string | null,
  handleRemoveFile: () => void,
  isMobile: boolean
): React.ReactElement | null => {
  if (!file) return null;
  
  if (fileType?.startsWith("video/") && previewUrl) {
    return <VideoPreview isMobile={isMobile} previewUrl={previewUrl} onRemove={handleRemoveFile} />;
  }
  if (fileType?.startsWith("audio/") && previewUrl) {
    return <AudioPreview isMobile={isMobile} previewUrl={previewUrl} onRemove={handleRemoveFile} />;
  }
  return null;
};

// Helper to render image model previews
const renderImageModelPreview = (
  allImagePreviews: string[] | undefined,
  onEditImages: (() => void) | undefined,
  handleRemoveImageAtIndex: (index: number) => void,
  handleAddImageInModal: () => void,
  isMobile: boolean
): React.ReactElement | null => {
  if (!allImagePreviews || allImagePreviews.length === 0) return null;
  
  return <StackedImagesPreview 
    imagePreviews={allImagePreviews} 
    onEdit={onEditImages}
    onRemoveImage={handleRemoveImageAtIndex}
    onAddImage={handleAddImageInModal}
    totalImages={3}
    isMobile={isMobile}
  />;
};

// Helper to render video model previews
const renderVideoModelPreview = (
  selectedModel: string,
  allImagePreviews: string[] | undefined,
  onEditImages: (() => void) | undefined,
  handleRemoveImageAtIndex: (index: number) => void,
  handleAddImageInModal: () => void,
  isMobile: boolean
): React.ReactElement | null => {
  if (!allImagePreviews || allImagePreviews.length === 0) return null;
  
  const modelSupportsMultiple = supportsMultipleImages(selectedModel as AIModel);
  
  return <StackedImagesPreview 
    imagePreviews={modelSupportsMultiple ? allImagePreviews : [allImagePreviews[0]]} 
    onEdit={onEditImages}
    onRemoveImage={handleRemoveImageAtIndex}
    onAddImage={modelSupportsMultiple ? handleAddImageInModal : undefined}
    totalImages={modelSupportsMultiple ? 3 : 1}
    isMobile={isMobile}
  />;
};

const renderMediaPreview = ({
  selectedModel,
  file,
  fileType,
  previewUrl,
  handleRemoveFile,
  allImagePreviews,
  onEditImages,
  isMobile,
  handleAddImageInModal,
  handleRemoveImageAtIndex,
}: {
  selectedModel: string;
  file: File | null;
  fileType: string | null;
  previewUrl: string | null;
  handleRemoveFile: () => void;
  imagePreview: string | null;
  onRemoveImage?: () => void;
  allImagePreviews?: string[];
  onEditImages?: () => void;
  isMobile: boolean;
  handleAddImageInModal: () => void;
  handleRemoveImageAtIndex: (index: number) => void;
}): React.ReactElement | null => {
  // Handle Whisper model file previews
  if (selectedModel === 'Whisper') {
    return renderWhisperPreview(file, fileType, previewUrl, handleRemoveFile, isMobile);
  }

  // Handle Gemini image models
  const isImageModel = IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]);
  if (isImageModel) {
    return renderImageModelPreview(allImagePreviews, onEditImages, handleRemoveImageAtIndex, handleAddImageInModal, isMobile);
  }

  // Handle video models
  return renderVideoModelPreview(selectedModel, allImagePreviews, onEditImages, handleRemoveImageAtIndex, handleAddImageInModal, isMobile);
};


// Aspect ratio selector component  
const AspectRatioSelector: FC<{
  selectedModel: string;
  transcription: any;
  aspectRatio: string;
  onAspectRatioChange?: (ratio: string) => void;
  isGenerating: boolean;
}> = ({ selectedModel, transcription, aspectRatio, onAspectRatioChange, isGenerating }) => {
  const { isOpen, setIsOpen, dropdownRef } = useDropdown();
  
  if (selectedModel === 'Whisper' || IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]) || transcription) {
    return null;
  }

  const aspectRatios = [
    { value: '16:9', label: '16:9', icon: '▭', category: 'Landscape' },
    { value: '9:16', label: '9:16', icon: '▯', category: 'Portrait' }
  ];

  const getDisplayContent = () => {
    const selected = aspectRatios.find(r => r.value === aspectRatio);
    if (!selected) return aspectRatio;
    
    // Determine if landscape or portrait for rectangle orientation
    const isLandscape = selected.value === '16:9';
    
    return (
      <div className="flex items-center" style={{ gap: '8px' }}>
        {/* Rectangle icon */}
        <svg 
          width={isLandscape ? "33" : "21"} 
          height={isLandscape ? "21" : "33"} 
          viewBox={isLandscape ? "0 0 33 21" : "0 0 21 33"} 
          fill="none"
        >
          <rect 
            x="1" 
            y="1" 
            width={isLandscape ? "31" : "19"} 
            height={isLandscape ? "19" : "31"} 
            rx="3" 
            stroke="currentColor" 
            strokeWidth="2"
          />
        </svg>
        {/* Ratio text */}
        <span style={{ fontSize: '16px', lineHeight: '24px' }}>{selected.label}</span>
      </div>
    );
  };

  const sizeStyles = {
    width: "138px",
    height: "42px",
    paddingTop: "9px",
    paddingRight: "27px",
    paddingBottom: "9px",
    paddingLeft: "27px",
    gap: "10px",
    borderRadius: "100px",
  };

  return (
    <div className="flex justify-center">
      <div className={`flex justify-start`}>
        <div className="relative" ref={dropdownRef}>
          {/* Dropdown Button */}
          <button
            onClick={() => !isGenerating && setIsOpen(!isOpen)}
            disabled={isGenerating}
            className="font-medium transition-all duration-300 focus:outline-none bg-white cursor-pointer flex items-center justify-center"
            style={{
              ...sizeStyles,
              ...GRADIENT_BORDER_STYLE,
              fontFamily: MAVEN_PRO_FONT,
            }}
          >
            {getDisplayContent()}
          </button>

          {/* Dropdown Menu */}
          {isOpen && (
            <div 
              className="absolute top-full mt-2 bg-white shadow-xl overflow-hidden z-50"
              style={{
                width: '177px',
                borderRadius: '25px',
                paddingTop: '14px',
                paddingRight: '8px',
                paddingBottom: '14px',
                paddingLeft: '8px',
                left: 'auto',
                right: '0',
                transform: 'none',
                ...GRADIENT_BORDER_STYLE,
              }}
            >
              {/* Header */}
              <div style={{ paddingLeft: '12px', paddingBottom: '16px' }}>
                <h3 
                  className="text-gray-800"
                  style={{
                    ...ASPECT_RATIO_SECTION_HEADER,
                    lineHeight: '100%',
                    letterSpacing: '0%',
                  }}
                >
                  Aspect Ratio
                </h3>
              </div>

              {/* Landscape Section */}
              <div>
                <div style={{ paddingLeft: '12px', paddingBottom: '8px' }}>
                  <p 
                    className="text-gray-500"
                    style={ASPECT_RATIO_SECTION_HEADER}
                  >
                    Landscape
                  </p>
                </div>
                <AspectRatioButton
                  ratio={{ value: '16:9', label: '16:9', width: 33, height: 21 }}
                  isSelected={aspectRatio === '16:9'}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAspectRatioChange?.('16:9');
                    setIsOpen(false);
                  }}
                />
              </div>

              {/* Portrait Section */}
              <div>
                <div style={{ paddingLeft: '12px', paddingBottom: '8px' }}>
                  <p 
                    className="text-gray-500"
                    style={ASPECT_RATIO_SECTION_HEADER}
                  >
                    Portrait
                  </p>
                </div>
                <AspectRatioButton
                  ratio={{ value: '9:16', label: '9:16', width: 21, height: 33 }}
                  isSelected={aspectRatio === '9:16'}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAspectRatioChange?.('9:16');
                    setIsOpen(false);
                  }}
                />
              </div>
            </div>
          )}

          {/* Click outside to close */}
          {isOpen && (
            <button
              type="button"
              className="fixed inset-0 z-40 bg-transparent border-0 p-0 cursor-default"
              onClick={() => setIsOpen(false)}
              onKeyDown={(e) => {
                if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
                  setIsOpen(false);
                }
              }}
              aria-label="Close aspect ratio selector"
              style={{ background: 'transparent' }}
            />
          )}
        </div>
      </div>
    </div>
  );
};// Image Aspect ratio selector component for Gemini 2.5
const ImageAspectRatioSelector: FC<{
  selectedModel: string;
  transcription: any;
  imageAspectRatio: string;
  onImageAspectRatioChange?: (ratio: string) => void;
  isGenerating: boolean;
  isMobile: boolean;
  isTablet: boolean;
}> = ({ selectedModel, transcription, imageAspectRatio, onImageAspectRatioChange, isGenerating, isMobile, isTablet }) => {
  const { isOpen, setIsOpen } = useDropdown();
  
  // Only show for image models
  if (!IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]) || transcription) {
    return null;
  }

  const aspectRatios = [
    // Square
    { value: '1:1', label: '1:1', width: 24, height: 24, category: 'Square' },
    // Landscape
    { value: '21:9', label: '21:9', width: 42, height: 18, category: 'Landscape' },
    { value: '16:9', label: '16:9', width: 33, height: 21, category: 'Landscape' },
    { value: '5:4', label: '5:4', width: 25, height: 20, category: 'Landscape' },
    { value: '4:3', label: '4:3', width: 28, height: 21, category: 'Landscape' },
    { value: '3:2', label: '3:2', width: 30, height: 20, category: 'Landscape' },
    // Portrait
    { value: '2:3', label: '2:3', width: 20, height: 30, category: 'Portrait' },
    { value: '3:4', label: '3:4', width: 21, height: 28, category: 'Portrait' },
    { value: '4:5', label: '4:5', width: 20, height: 25, category: 'Portrait' },
    { value: '9:16', label: '9:16', width: 21, height: 33, category: 'Portrait' }
  ];

  const getDisplayContent = () => {
    const selected = aspectRatios.find(r => r.value === imageAspectRatio);
    if (!selected) return imageAspectRatio;
    
    return (
      <div className="flex items-center" style={{ gap: '8px' }}>
        {/* Rectangle icon */}
        <svg 
          width={selected.width} 
          height={selected.height} 
          viewBox={`0 0 ${selected.width} ${selected.height}`} 
          fill="none"
        >
          <rect 
            x="1" 
            y="1" 
            width={selected.width - 2} 
            height={selected.height - 2} 
            rx="3" 
            stroke="currentColor" 
            strokeWidth="2"
          />
        </svg>
        {/* Ratio text */}
        <span style={{ fontSize: '16px', lineHeight: '24px' }}>{selected.label}</span>
      </div>
    );
  };

  // Group ratios by category
  const squareRatios = aspectRatios.filter(r => r.category === 'Square');
  const landscapeRatios = aspectRatios.filter(r => r.category === 'Landscape');
  const portraitRatios = aspectRatios.filter(r => r.category === 'Portrait');

  return (
    <DropdownWrapper
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      isGenerating={isGenerating}
      buttonWidth="138px"
      displayContent={getDisplayContent()}
      ariaLabel="Close image aspect ratio selector"
      isMobile={isMobile}
      isTablet={isTablet}
    >
      <div 
        className="absolute top-full mt-2 bg-white shadow-xl z-50"
        style={{
          width: '177px',
          ...DROPDOWN_MENU_BASE_STYLE,
          ...GRADIENT_BORDER_STYLE,
        }}
      >
        {/* Header */}
        <div style={{ paddingLeft: '12px' }}>
          <h3
            className="text-gray-800"
            style={{
              ...ASPECT_RATIO_SECTION_HEADER,
              fontStyle: 'normal',
              lineHeight: '100%',
              letterSpacing: '0%',
            }}
          >
            Aspect Ratio
          </h3>
        </div>

        {/* Square Section */}
        {squareRatios.length > 0 && (
          <div>
            <div style={SECTION_HEADER_CONTAINER_STYLE}>
              <p className="text-gray-500" style={ASPECT_RATIO_SECTION_HEADER}>
                Square
              </p>
            </div>
            <div style={FLEX_COLUMN_GAP_4_STYLE}>
              {squareRatios.map((ratio) => (
                <AspectRatioButton
                  key={ratio.value}
                  ratio={ratio}
                  isSelected={imageAspectRatio === ratio.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    onImageAspectRatioChange?.(ratio.value);
                    setIsOpen(false);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Landscape Section */}
        <div>
          <div style={SECTION_HEADER_CONTAINER_STYLE}>
            <p className="text-gray-500" style={ASPECT_RATIO_SECTION_HEADER}>
              Landscape
            </p>
          </div>
          <div style={FLEX_COLUMN_GAP_4_STYLE}>
            {landscapeRatios.map((ratio) => (
              <AspectRatioButton
                key={ratio.value}
                ratio={ratio}
                isSelected={imageAspectRatio === ratio.value}
                onClick={(e) => {
                  e.stopPropagation();
                  onImageAspectRatioChange?.(ratio.value);
                  setIsOpen(false);
                }}
              />
            ))}
          </div>
        </div>

        {/* Portrait Section */}
        <div>
          <div style={SECTION_HEADER_CONTAINER_STYLE}>
            <p className="text-gray-500" style={ASPECT_RATIO_SECTION_HEADER}>
              Portrait
            </p>
          </div>
          <div style={FLEX_COLUMN_GAP_4_STYLE}>
            {portraitRatios.map((ratio) => (
              <AspectRatioButton
                key={ratio.value}
                ratio={ratio}
                isSelected={imageAspectRatio === ratio.value}
                onClick={(e) => {
                  e.stopPropagation();
                  onImageAspectRatioChange?.(ratio.value);
                  setIsOpen(false);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </DropdownWrapper>
  );
};

// Image Resolution selector component for Gemini 2.5
const ImageResolutionSelector: FC<{
  selectedModel: string;
  transcription: any;
  imageResolution: string;
  onImageResolutionChange?: (resolution: string) => void;
  isGenerating: boolean;
  isMobile: boolean;
  isTablet: boolean;
}> = ({ selectedModel, transcription, imageResolution, onImageResolutionChange, isGenerating, isMobile, isTablet }) => {
  const { isOpen, setIsOpen } = useDropdown();
  
  // Only show for image models
  if (!IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]) || transcription) {
    return null;
  }

  const resolutions = [
    { value: '1k', label: '1K' },
    { value: '2k', label: '2K' },
    { value: '4k', label: '4K' }
  ];

  const getDisplayContent = () => {
    const selected = resolutions.find(r => r.value === imageResolution);
    if (!selected) return imageResolution;
    
    return (
      <span style={{ fontSize: '16px', lineHeight: '24px' }}>{selected.label}</span>
    );
  };

  return (
    <DropdownWrapper
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      isGenerating={isGenerating}
      buttonWidth="80px"
      displayContent={getDisplayContent()}
      ariaLabel="Close image resolution selector"
      isMobile={isMobile}
      isTablet={isTablet}
    >
      <div 
        className="absolute top-full mt-2 bg-white shadow-xl z-50"
        style={{
          width: '120px',
          ...DROPDOWN_MENU_BASE_STYLE,
          ...GRADIENT_BORDER_STYLE,
        }}
      >
        {/* Header */}
        <div style={{ paddingLeft: '12px' }}>
          <h3
            className="text-gray-800"
            style={{
              ...ASPECT_RATIO_SECTION_HEADER,
              fontStyle: 'normal',
              lineHeight: '100%',
              letterSpacing: '0%',
            }}
          >
            Resolution
          </h3>
        </div>

        {/* Resolution Options */}
        <div>
          <div style={FLEX_COLUMN_GAP_4_STYLE}>
            {resolutions.map((resolution) => (
              <AspectRatioButton
                key={resolution.value}
                label={resolution.label}
                isSelected={imageResolution === resolution.value}
                justifyContent="center"
                onClick={(e) => {
                  e.stopPropagation();
                  onImageResolutionChange?.(resolution.value);
                  setIsOpen(false);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </DropdownWrapper>
  );
};

// Main generate button component
const MainGenerateButton: FC<{
  onGenerate: () => void;
  isTranscription: boolean;
  isGenerating: boolean;
  isTranscriptionLoading?: boolean;
  isProcessingLastFrame?: boolean;
  selectedModel: string;
  previewUrl: string | null;
  prompt?: string;
}> = ({ 
  onGenerate, 
  isTranscription, 
  isGenerating, 
  isTranscriptionLoading,
  isProcessingLastFrame = false,
  selectedModel, 
  previewUrl,
  prompt = '',
}) => {
  const isDisabled = isTranscription || 
    isGenerating || 
    isTranscriptionLoading ||
    isProcessingLastFrame ||
    (selectedModel === 'Whisper' && !previewUrl) ||
    !prompt.trim();
    
  return (
    <button
      onClick={onGenerate}
      disabled={isDisabled}
      className="flex-shrink-0 transition-all duration-300 hover:scale-105 ml-auto flex items-center justify-center"
      style={{
        width: '35px',
        height: '35px',
        gap: '10px',
        opacity: isDisabled ? 0.5 : 1,
        borderRadius: '100px',
        padding: '0',
        background: 'linear-gradient(135deg, #F39A81 0%, #959BEA 100%)',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        outline: 'none',
      }}
      onMouseDown={(e) => e.preventDefault()}
      tabIndex={-1}
    >
      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
      </svg>
    </button>
  );
};

// Helper function to process file uploads based on model type
const processFileUpload = (
  file: File,
  selectedModel: string,
  previewUrl: string | null,
  imagePreview: string | null | undefined,
  handleFileSelection: (file: File) => void,
  onImageUpload: ((file: File) => void) | undefined
): void => {
  // Whisper/Transcript: Only 1 audio/video file
  if (selectedModel === 'Whisper') {
    if (previewUrl) {
      alert('Only 1 audio/video file allowed for transcription');
      return;
    }
    if (validateWhisperFile(file, () => {})) {
      handleWhisperFile(file, handleFileSelection);
    }
    return;
  }
  
  // Video models (Veo 3, Veo 3 Fast, Veo 2, Hunyuan): Only 1 image
  const isVideoModel = selectedModel === 'Veo 3' || selectedModel === 'Veo 3 Fast' || selectedModel === 'Veo 2' || selectedModel === 'Hunyuan';
  if (isVideoModel && imagePreview) {
    alert('Only 1 image allowed for video generation');
    return;
  }
  
  // Image models: Max 3 images
  if (validateImageFile(file, () => {})) {
    if (onImageUpload) {
      handleImageFile(file, onImageUpload, () => {});
    }
  }
};

interface MobileModelLayoutProps {
  selectedModel: string;
  orderedModels: string[];
  isGenerating: boolean;
  isTranscription: boolean;
  isTranscriptionLoading: boolean | undefined;
  isProcessingLastFrame?: boolean;
  isMobile: boolean;

  transcription: Transcription | null;
  prompt: string;
  previewUrl: string | null;
  mediaPreview?: React.ReactNode;
  imageUploadTitle?: string;
  isAudioEnabled?: boolean;
  generateAudio?: boolean;

  // Handlers
  onGenerate: () => void;
  handleImageClick: () => void;
  handleModelSelect: (model: string) => void;
  onPromptChange: (s: string) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleAudioClick: () => void;
  handleOpenPromptSettings: () => void;
  onSaveTranscription?: (data: any) => void;
}

const MobileModelLayout: FC<MobileModelLayoutProps> = ({
  selectedModel,
  orderedModels,
  isGenerating,
  isTranscription,
  isTranscriptionLoading,
  isProcessingLastFrame = false,
  isMobile,
  transcription,
  prompt,
  previewUrl,
  mediaPreview,
  imageUploadTitle,
  isAudioEnabled,
  generateAudio,
  onGenerate,
  handleImageClick,
  handleModelSelect,
  onPromptChange,
  handleKeyDown,
  handleAudioClick,
  handleOpenPromptSettings,
  onSaveTranscription,
}) => {
  const isWhisper = selectedModel === "Whisper";

  return (
    <div className="flex flex-col items-center w-full gap-4 min-w-[359px]">
      <MobileModelSelector
        orderedModels={orderedModels}
        selectedModel={selectedModel}
        onModelSelect={handleModelSelect}
        isGenerating={isGenerating}
      />

      {isWhisper ? (
        <div className='w-full'>
          <p className='font-maven text-[#C6C6C6] mb-4'>
            {isTranscriptionLoading ? <LoadingDots /> : 'Attach video and wait for your transcript'}
          </p>
          <div
            className="w-full p-3"
            style={{
              borderRadius: "30px",
              border: "2px solid transparent",
              background:
              "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            }}
          >
            <TranscriptionInput
              previewUrl={previewUrl}
              isGenerating={isGenerating}
              isTranscriptionLoading={isTranscriptionLoading}
              isTranscription={isTranscription}
              onGenerate={onGenerate}
              handleImageClick={handleImageClick}
              imageUploadTitle={imageUploadTitle}
              selectedModel={selectedModel}
              isMobile={isMobile}
            />
          </div>
        </div>
      ) : (
        <div className="w-full mx-auto">
          {transcription ? (
            <ExpandableText
              title={transcription.title || "Untitled"}
              text={transcription.transcription || ""}
              readOnly={true}
              onSave={onSaveTranscription}
            />
          ) : (
            <PromptInput
              value={prompt}
              onChange={onPromptChange}
              onKeyDown={handleKeyDown}
              disabled={isGenerating || isWhisper}
              showAudioIcon={!IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number])}
              isAudioEnabled={isAudioEnabled}
              isAudioActive={generateAudio && isAudioEnabled}
              onAudioClick={handleAudioClick}
              handleImageClick={handleImageClick}
              imageUploadTitle={imageUploadTitle}
              handleTuneClick={handleOpenPromptSettings}
              renderGenerateButton={
                <MainGenerateButton
                  onGenerate={onGenerate}
                  isTranscription={isTranscription}
                  isGenerating={isGenerating}
                  isTranscriptionLoading={isTranscriptionLoading}
                  isProcessingLastFrame={isProcessingLastFrame}
                  selectedModel={selectedModel}
                  previewUrl={previewUrl}
                  prompt={prompt}
                />
              }
            />
          )}
        </div>
      )}

      {mediaPreview}
    </div>
  );
};

interface PicassoControlsProps {
  transcription: Transcription | null;
  onSaveTranscription: (transcription: Transcription) => void;
  prompt: string;
  onPromptChange: (value: string) => void;
  selectedModel: string;
  onModelSelect: (model: string) => void;
  orderedModels: string[];
  onGenerate: () => void;
  isGenerating?: boolean;
  isProcessingLastFrame?: boolean;
  imagePreview?: string | null;
  onImageUpload?: (file: File) => void;
  onRemoveImage?: () => void;
  onRemoveImageAtIndex?: (index: number) => void;
  generateAudio?: boolean;
  onToggleAudio?: () => void;
  aspectRatio?: string;
  onAspectRatioChange?: (ratio: string) => void;
  imageAspectRatio?: string;
  onImageAspectRatioChange?: (ratio: string) => void;
  imageResolution?: string;
  onImageResolutionChange?: (resolution: string) => void;
  file: File | null;
  isTranscriptionLoading?: boolean;
  handleFileSelection: (file: File) => void;
  handleRemoveFile: () => void;
  previewUrl: string | null;
  isTranscription: boolean;
  fileType: string | null;
  pageValue?: string;
  onPageChange?: (event: React.MouseEvent<HTMLElement>, newValue: string | null) => void;
  uploadedImagesCount?: number;
  allImagePreviews?: string[];
}

const PicassoControls: FC<PicassoControlsProps> = ({
  isTranscriptionLoading,
  isProcessingLastFrame = false,
  transcription,
  onSaveTranscription,
  prompt,
  onPromptChange,
  selectedModel,
  onModelSelect,
  orderedModels,
  onGenerate,
  isGenerating = false,
  imagePreview,
  onImageUpload,
  onRemoveImage,
  onRemoveImageAtIndex,
  generateAudio = true,
  onToggleAudio,
  aspectRatio = '16:9',
  onAspectRatioChange,
  imageAspectRatio = '1:1',
  onImageAspectRatioChange,
  imageResolution = '1k',
  onImageResolutionChange,
  file,
  handleFileSelection,
  handleRemoveFile,
  previewUrl,
  isTranscription,
  fileType,
  pageValue = "Videos",
  onPageChange,
  uploadedImagesCount = 0,
  allImagePreviews = [],
}) => { // NOSONAR - Complex UI component with multiple conditional renderings
  const { isTablet, isMobile } = use(DeviceContext) ?? { isTablet: false, isMobile: false };
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isPromptSettingsOpen, setIsPromptSettingsOpen] = useState(false);

  const handleModelSelect = useCallback((model: string) => {
    onModelSelect(model);
  }, [onModelSelect]);

  const handleOpenImageModal = useCallback(() => {
    setIsImageModalOpen(true);
  }, []);

  const handleCloseImageModal = useCallback(() => {
    setIsImageModalOpen(false);
  }, []);

  const handleOpenPromptSettings = useCallback(() => {
    setIsPromptSettingsOpen(true);
  }, []);

  const handleClosePromptSettings = useCallback(() => {
    setIsPromptSettingsOpen(false);
  }, []);

  const handleAddImageInModal = useCallback(() => {
    // Check max images based on model
    let maxImages = 3; // Default for Images tab
    if (pageValue === 'Videos') {
      maxImages = supportsMultipleImages(selectedModel as AIModel) ? 3 : 1;
    }
    
    if (uploadedImagesCount >= maxImages) return;
    
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true; // Allow multiple file selection
    input.style.display = 'none';
    
    const handleChange = (e: Event) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && onImageUpload) {
        // Upload files one by one, respecting the max image limit
        Array.from(files).forEach((file, index) => {
          if (uploadedImagesCount + index < maxImages) {
            onImageUpload(file);
          }
        });
      }
      // Clean up
      input.removeEventListener('change', handleChange);
      input.remove();
    };
    
    input.addEventListener('change', handleChange);
    document.body.appendChild(input);
    input.click();
  }, [uploadedImagesCount, onImageUpload]);

  const handleRemoveImageAtIndex = useCallback((index: number) => {
    if (onRemoveImageAtIndex) {
      onRemoveImageAtIndex(index);
    }
  }, [onRemoveImageAtIndex]);

  const handleFileUpload = useCallback((event: Event) => {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) return;

    // For Images tab, allow multiple file uploads
    if (pageValue === 'Images' && files.length > 1) {
      Array.from(files).forEach((file) => {
        if (onImageUpload) {
          onImageUpload(file);
        }
      });
    } else if (pageValue === 'Videos' && files.length > 1 && onImageUpload) {
      // For Videos tab, check if model supports multiple images
      if (supportsMultipleImages(selectedModel as AIModel)) {
        Array.from(files).forEach((file) => {
          onImageUpload(file);
        });
      } else {
        // Single image model - only upload first file
        const file = files[0];
        processFileUpload(file, selectedModel, previewUrl, imagePreview, handleFileSelection, onImageUpload);
      }
    } else {
      // For other models, process single file
      const file = files[0];
      processFileUpload(file, selectedModel, previewUrl, imagePreview, handleFileSelection, onImageUpload);
    }
  }, [selectedModel, handleFileSelection, onImageUpload, previewUrl, imagePreview, pageValue]);

  const handleImageClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = selectedModel === 'Whisper' ? 'video/*,audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac' : 'image/*';
    input.style.display = 'none';
    
    // Allow multiple file selection only for models that support it
    if (pageValue === 'Images') {
      input.multiple = true;
    } else if (pageValue === 'Videos') {
      // Check if the current video model supports multiple images
      input.multiple = supportsMultipleImages(selectedModel as AIModel);
    }
    
    const handleChange = (e: Event) => {
      handleFileUpload(e);
      // Clean up
      input.removeEventListener('change', handleChange);
      input.remove();
    };
    
    input.addEventListener('change', handleChange);
    document.body.appendChild(input);
    input.click();
  }, [selectedModel, handleFileUpload, pageValue]);

  const handleAudioClick = useCallback(() => {
    onToggleAudio?.();
  }, [onToggleAudio]);

  const handleRefresh = useCallback(() => {
    globalThis.location.reload();
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !isGenerating && prompt.trim()) {
      onGenerate();
    }
  }, [isGenerating, prompt, onGenerate]);

  const isAudioEnabled = useMemo(() => 
    isAudioEnabledForModel(selectedModel), 
    [selectedModel]
  );

  const imageUploadTitle = useMemo(() => {
    return getImageUploadTitle(selectedModel, pageValue);
  }, [selectedModel, pageValue]);

  const mediaPreview = useMemo(() => {
    return renderMediaPreview({
      selectedModel,
      file,
      fileType,
      previewUrl,
      handleRemoveFile,
      imagePreview: imagePreview || null,
      onRemoveImage,
      allImagePreviews,
      onEditImages: handleOpenImageModal,
      isMobile,
      handleAddImageInModal,
      handleRemoveImageAtIndex,
    });
  }, [
    selectedModel,
    file,
    fileType,
    previewUrl,
    handleRemoveFile,
    imagePreview,
    onRemoveImage,
    allImagePreviews,
    handleOpenImageModal,
    isMobile,
    handleAddImageInModal,
    handleRemoveImageAtIndex,
  ]);

  return (
    <div className="text-center mb-6">
      {/* Wrapper for title, tabs, and input */}
      <div>
        {!isMobile && <div className='w-full flex justify-center'>
          <PicassoTitle
            size="lg"
            onClick={handleRefresh}
            style={{ maxWidth: "fit-content" }}
            className={`${isMobile || isTablet ? "" : "mb-[1.5rem]"} cursor-pointer`}
          />
        </div>}

        {onPageChange && (
          <div className="flex justify-center mb-10 mt-10">
            <TogglePage value={pageValue} handleChange={onPageChange} />
          </div>
        )}

        {/* New Unified Input Container Design */}
        <div 
          className={`flex items-center gap-6 mt-6 w-full ${isMobile || isTablet ? "px-4 justify-center" : ""}`} 
          style={isMobile || isTablet ? {} : { paddingLeft: 'calc(50% - 393px)' }}
        >
        {isMobile || isTablet ? 
          <MobileModelLayout
            selectedModel={selectedModel}
            orderedModels={orderedModels}
            isGenerating={isGenerating}
            isTranscription={isTranscription}
            isTranscriptionLoading={isTranscriptionLoading}
            isProcessingLastFrame={isProcessingLastFrame}
            isMobile={isMobile}
            transcription={transcription}
            prompt={prompt}
            previewUrl={previewUrl}
            mediaPreview={mediaPreview}
            imageUploadTitle={imageUploadTitle}
            isAudioEnabled={isAudioEnabled}
            generateAudio={generateAudio}
            onGenerate={onGenerate}
            handleImageClick={handleImageClick}
            handleModelSelect={handleModelSelect}
            onPromptChange={onPromptChange}
            handleKeyDown={handleKeyDown}
            handleAudioClick={handleAudioClick}
            handleOpenPromptSettings={handleOpenPromptSettings}
            onSaveTranscription={onSaveTranscription}
          />
          : 
          <>
            {/* Tab toggle for Videos/Images/Transcripts */}
            <section 
             className={`flex ${selectedModel === 'Whisper' ? 'items-center' : 'flex-col justify-between'}`}
              style={{
                width: '786px',
                maxWidth: '786px',
                minWidth: '786px',
                height: selectedModel === 'Whisper' ? '78px' : '125px',
                padding: selectedModel === 'Whisper' ? '18px 20px' : '16px 24px',
                gap: selectedModel === 'Whisper' ? '23px' : undefined,
                borderRadius: '30px',
                border: "2px solid transparent",
                background: "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
              }}
              aria-label="File drop zone"
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => { // NOSONAR - File drop handler with model-specific logic
                e.preventDefault();
                const files = e.dataTransfer.files;
                if (files && files.length > 0) {
                  // Whisper/Transcript: Only 1 audio/video file
                  if (selectedModel === 'Whisper') {
                    const file = files[0];
                    if (previewUrl) {
                      alert('Only 1 audio/video file allowed for transcription');
                      return;
                    }
                    if (validateWhisperFile(file, () => {})) {
                      handleWhisperFile(file, handleFileSelection);
                    }
                    return;
                  }
                  
                  // Video models: Up to 3 images for models that support multiple, 1 for others
                  const isVideoModel = selectedModel === 'Veo 3' || selectedModel === 'Veo 3 Fast' || selectedModel === 'Veo 2' || selectedModel === 'Hunyuan' || selectedModel === 'Veo 3.1' || selectedModel === 'Veo 3.1 Fast' || selectedModel === 'Mochi' || selectedModel === 'Opensora' || selectedModel === 'Wan 2.2';
                  if (isVideoModel && onImageUpload) {
                    const maxImages = supportsMultipleImages(selectedModel as AIModel) ? 3 : 1;
                    const currentImageCount = allImagePreviews?.length || 0;
                    const allowedCount = Math.min(files.length, maxImages - currentImageCount);
                    
                    if (currentImageCount >= maxImages) {
                      alert(`Maximum ${maxImages} image${maxImages > 1 ? 's' : ''} allowed for ${selectedModel}`);
                      return;
                    }
                    
                    for (let i = 0; i < allowedCount; i++) {
                      const file = files[i];
                      if (validateImageFile(file, () => {})) {
                        handleImageFile(file, onImageUpload, () => {});
                      }
                    }
                    return;
                  }
                  
                  // Image model (Gemini 2.5): Max 3 images - allow multiple files
                  if (pageValue === 'Images' && onImageUpload) {
                    Array.from(files).forEach((file) => {
                      if (validateImageFile(file, () => {})) {
                        handleImageFile(file, onImageUpload, () => {});
                      }
                    });
                  }
                }
              }}
            >
              {/* Conditional Layout based on model */}
              {selectedModel === 'Whisper' ? (
                <TranscriptionInput
                  previewUrl={previewUrl}
                  isGenerating={isGenerating}
                  isTranscriptionLoading={isTranscriptionLoading}
                  isTranscription={isTranscription}
                  onGenerate={onGenerate}
                  handleImageClick={handleImageClick}
                  imageUploadTitle={imageUploadTitle}
                  selectedModel={selectedModel}
                  isMobile={isMobile}
                />
              ) : (
                // Videos & Images - Two Row Layout
                <>
                  {/* Top Row - Prompt Input */}
                  <div className="flex items-center gap-4">
                    {transcription ? (
                      <div className="flex-1">
                        <ExpandableText 
                          title={transcription?.title || 'Untitled'} 
                          text={transcription?.transcription || ''} 
                          readOnly={true} 
                          onSave={onSaveTranscription} 
                        />
                      </div>
                    ) : (
                      <input
                        id="prompt-input"
                        type="text"
                        value={prompt}
                        onChange={(e) => onPromptChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        disabled={isGenerating}
                        placeholder="Ready when you are..."
                        className="flex-1 text-base text-gray-700 placeholder-gray-400 focus:outline-none bg-transparent border-none"
                        style={{ fontSize: '16px' }}
                      />
                    )}
                  </div>

                  {/* Bottom Row - Controls (Image, Model, Aspect Ratio, Audio) with Arrow Button */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Image Upload Icon - Styled like other buttons */}
                      <button
                        onClick={handleImageClick}
                        className="flex-shrink-0 transition-all duration-300 hover:scale-105 flex items-center justify-center focus:outline-none outline-none"
                        title={imageUploadTitle}
                        style={{
                          width: '44px',
                          height: '42px',
                          paddingTop: '9px',
                          paddingRight: '10px',
                          paddingBottom: '9px',
                          paddingLeft: '10px',
                          gap: '10px',
                          borderRadius: '100px',
                          borderWidth: '2px',
                          border: "2px solid transparent",
                          background: "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box",
                          opacity: 1,
                          outline: 'none'
                        }}
                      >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </button>

                      {/* Model Selector - All models including Image models */}
                      <div className="flex-shrink-0">
                        <ModelSelector
                          models={orderedModels}
                          selectedModel={selectedModel}
                          onModelSelect={handleModelSelect}
                          disabled={isGenerating}
                        />
                      </div>

                      {/* Aspect Ratio Dropdown */}
                      {!IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]) && selectedModel !== 'Whisper' && !transcription && (
                        <div className="flex-shrink-0">
                          <AspectRatioSelector
                            selectedModel={selectedModel}
                            transcription={transcription}
                            aspectRatio={aspectRatio}
                            onAspectRatioChange={onAspectRatioChange}
                            isGenerating={isGenerating}
                          />
                        </div>
                      )}

                      {/* Image Aspect Ratio Dropdown - for Image models */}
                      {IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]) && !transcription && (
                        <div className="flex-shrink-0">
                          <ImageAspectRatioSelector
                            selectedModel={selectedModel}
                            transcription={transcription}
                            imageAspectRatio={imageAspectRatio}
                            onImageAspectRatioChange={onImageAspectRatioChange}
                            isGenerating={isGenerating}
                            isMobile={false}
                            isTablet={false}
                          />
                        </div>
                      )}

                      {/* Image Resolution Dropdown - for Gemini 3 Pro only */}
                      {selectedModel === 'Gemini 3 Pro' && !transcription && (
                        <div className="flex-shrink-0">
                          <ImageResolutionSelector
                            selectedModel={selectedModel}
                            transcription={transcription}
                            imageResolution={imageResolution}
                            onImageResolutionChange={onImageResolutionChange}
                            isGenerating={isGenerating}
                            isMobile={false}
                            isTablet={false}
                          />
                        </div>
                      )}

                      {/* Audio Toggle Icon */}
                      {!IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]) && isAudioEnabled && (
                        <button
                          type="button"
                          onClick={handleAudioClick}
                          className="flex-shrink-0 focus:outline-none"
                          title="Toggle audio generation"
                          style={{ 
                            background: 'transparent', 
                            border: 'none',
                            padding: 0,
                            outline: 'none',
                          }}
                        >
                          <svg 
                            style={{ color: '#8B8B8B' }}
                            className="w-6 h-6"
                            fill="currentColor" 
                            viewBox="0 0 24 24" 
                            height="24px" 
                            width="24px"
                          >
                            {generateAudio ? (
                              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                            ) : (
                              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                            )}
                          </svg>
                        </button>
                      )}
                    </div>

                    {/* Arrow Submit Button - Gradient circle with RIGHT arrow */}
                    <button
                      onClick={onGenerate}
                      disabled={
                        isTranscription || 
                        isGenerating || 
                        isTranscriptionLoading || 
                        isProcessingLastFrame ||
                        !prompt.trim()
                      }
                      className="flex-shrink-0 p-3 rounded-full transition-all duration-300 hover:scale-105"
                      style={{
                        background: 'linear-gradient(135deg, #F39A81 0%, #959BEA 100%)',
                        opacity: (isTranscription || isGenerating || isTranscriptionLoading || isProcessingLastFrame || !prompt.trim()) ? 0.5 : 1,
                        cursor: (isTranscription || isGenerating || isTranscriptionLoading || isProcessingLastFrame || !prompt.trim()) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </button>
                  </div>
                </>
              )}
            </section>          {/* Image/Video Preview Tile - Outside the input box on the right */}
            {mediaPreview && (
              <div className="flex-shrink-0">
                {mediaPreview}
              </div>
            )}
          </>
        }
      </div>
      </div>
      {/* End of blur wrapper */}

      {/* Image Manager Modal */}
      <ImageManagerModal
        isOpen={isImageModalOpen}
        onClose={handleCloseImageModal}
        imagePreviews={allImagePreviews}
        totalImages={uploadedImagesCount}
        maxImages={(() => {
          if (pageValue === 'Videos') {
            return supportsMultipleImages(selectedModel as AIModel) ? 3 : 1;
          }
          return 3;
        })()}
        onRemoveImage={handleRemoveImageAtIndex}
        onAddImage={handleAddImageInModal}
      />

      {/* Mobile Prompt Settings Drawer */}
      <MobilePromptSettingsDrawer
        isOpen={isPromptSettingsOpen}
        onClose={handleClosePromptSettings}
        aspectRatio={IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]) ? imageAspectRatio : aspectRatio}
        onAspectRatioChange={IMAGE_MODELS.includes(selectedModel as typeof IMAGE_MODELS[number]) ? onImageAspectRatioChange : onAspectRatioChange}
        imageResolution={imageResolution}
        onImageResolutionChange={onImageResolutionChange}
        generateAudio={generateAudio}
        isAudioEnabled={isAudioEnabled}
        onAudioToggle={onToggleAudio}
        selectedModel={selectedModel}
        transcription={transcription}
      />

    </div>
  );
};

export default PicassoControls;
